import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/shared/Layout';
import Login        from './pages/Login';
import Dashboard    from './pages/Dashboard';
import Tickets      from './pages/Tickets';
import TicketDetail from './pages/TicketDetail';
import NewTicket    from './pages/NewTicket';
import Observability    from './pages/Observability/Observability';
import Clients          from './pages/Clients/Clients';
import Notifications    from './pages/Notifications/Notifications';
import SlaAdmin         from './pages/SLA/SlaAdmin';
import PlantChecklist    from './pages/business/Checklist/PlantChecklist';
import SupplierDetail    from './pages/business/Suppliers/SupplierDetail';
import SuppliersList     from './pages/business/Suppliers/SuppliersList';
import RedirectTicket    from './pages/business/Redirect/RedirectTicket';
import SupplierPortal    from './pages/business/SupplierPortal/SupplierPortal';
import ReportIssue       from './pages/business/ReportIssue/ReportIssue';
import NewBusinessTicket from './pages/business/Tickets/NewBusinessTicket';
import BusinessTickets      from './pages/business/Tickets/BusinessTickets';
import BusinessTicketDetail from './pages/business/Tickets/BusinessTicketDetail';
import BusinessDashboard    from './pages/business/Dashboard/BusinessDashboard';
import TrackerImport        from './pages/business/Admin/TrackerImport';
import TrialRequest     from './pages/TrialRequest/TrialRequest';
import Trials           from './pages/Trials/Trials';
import TrialDetail      from './pages/Trials/TrialDetail';
import DemoRequest      from './pages/DemoRequest/DemoRequest';
import Demos            from './pages/Demos/Demos';
import DemoDetail       from './pages/Demos/DemoDetail';
import './styles/globals.css';

function ProtectedRoute({ children, adminOnly = false, businessOnly = false }) {
  const { user, loading, isAdmin, isBusinessSupport } = useAuth();
  if (loading) return <div style={{ padding:48, color:'var(--text-muted)', textAlign:'center' }}>Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && !isAdmin) return <Navigate to="/tickets" replace />;
  if (businessOnly && !isBusinessSupport) return <Navigate to="/dashboard" replace />;
  return <Layout>{children}</Layout>;
}

// Default landing page by role — mirrors the redirect in Login.jsx (Task 11).
// business_support_lead/viewer land on their own vertical; client_user keeps its existing
// /tickets redirect; everyone else (support roles, admin, superadmin) keeps /dashboard.
function homeFor(role) {
  if (role === 'client_user') return '/tickets';
  if (['business_support_lead', 'business_support_viewer'].includes(role)) return '/business/dashboard';
  return '/dashboard';
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login"        element={user ? <Navigate to={homeFor(user.role)} replace /> : <Login />} />
      <Route path="/business/redirect/:token" element={<RedirectTicket />} />
      <Route path="/business/supplier-portal/:token" element={<SupplierPortal />} />
      <Route path="/business/report-issue" element={<ReportIssue />} />
      <Route path="/trial-request" element={<TrialRequest />} />
      <Route path="/demo-request" element={<DemoRequest />} />
      <Route path="/trials"       element={<ProtectedRoute adminOnly><Trials /></ProtectedRoute>} />
      <Route path="/trials/:id"   element={<ProtectedRoute adminOnly><TrialDetail /></ProtectedRoute>} />
      <Route path="/demos"        element={<ProtectedRoute adminOnly><Demos /></ProtectedRoute>} />
      <Route path="/demos/:id"    element={<ProtectedRoute adminOnly><DemoDetail /></ProtectedRoute>} />
      <Route path="/dashboard"    element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/tickets"      element={<ProtectedRoute><Tickets /></ProtectedRoute>} />
      <Route path="/tickets/new"  element={<ProtectedRoute><NewTicket /></ProtectedRoute>} />
      <Route path="/tickets/:id"  element={<ProtectedRoute><TicketDetail /></ProtectedRoute>} />
      <Route path="/observability" element={<ProtectedRoute><Observability /></ProtectedRoute>} />
      <Route path="/clients"      element={<ProtectedRoute adminOnly><Clients /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute adminOnly><Notifications /></ProtectedRoute>} />
      <Route path="/sla"          element={<ProtectedRoute adminOnly><SlaAdmin /></ProtectedRoute>} />
      <Route path="/business/dashboard"        element={<ProtectedRoute businessOnly><BusinessDashboard /></ProtectedRoute>} />
      <Route path="/business/admin/import"     element={<ProtectedRoute businessOnly><TrackerImport /></ProtectedRoute>} />
      <Route path="/business/checklist"        element={<ProtectedRoute businessOnly><PlantChecklist /></ProtectedRoute>} />
      <Route path="/business/suppliers"         element={<ProtectedRoute businessOnly><SuppliersList /></ProtectedRoute>} />
      <Route path="/business/suppliers/:id"    element={<ProtectedRoute businessOnly><SupplierDetail /></ProtectedRoute>} />
      <Route path="/business/tickets/new"      element={<ProtectedRoute businessOnly><NewBusinessTicket /></ProtectedRoute>} />
      <Route path="/business/tickets"          element={<ProtectedRoute businessOnly><BusinessTickets /></ProtectedRoute>} />
      <Route path="/business/tickets/:id"      element={<ProtectedRoute businessOnly><BusinessTicketDetail /></ProtectedRoute>} />
      <Route path="/"             element={user ? <Navigate to={homeFor(user.role)} replace /> : <Navigate to="/login" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
